import { useNavigate } from 'react-router-dom'
import { SectionHeader } from '@/shared/ui/SectionHeader'
import { TextField } from '@/shared/ui/TextField'
import type { OrgNewDraft } from '@/public/features/org/types'
import {
  CARD_ACCESS_MAX,
  CARD_SUMMARY_MAX,
  CARD_TEXT_MAX,
  CARD_TITLE_MAX,
  ORG_LAST_STEP,
} from '@/public/features/org/constants/orgNew'
import {
  OrgNewPcShell,
  PcTextarea,
  PC_ASSISTIVE_BUTTON_CLASS,
  PC_PRIMARY_BUTTON_CLASS,
} from '@/public/features/org/ui/orgNewPc/parts'

/**
 * PC 세 번째 페이지 — 모바일 5단계(해결카드 본문)에 대응한다(시안 1288:21739).
 * 문구·상한은 모바일 `StepCardContent` 와 같은 상수를 쓴다.
 */
export function OrgNewPcCardContent({ draft }: { draft: OrgNewDraft }) {
  const navigate = useNavigate()

  return (
    <OrgNewPcShell
      title="해결카드 등록하기"
      active="card-input"
      error={draft.error}
      footer={
        <>
          <button
            type="button"
            className={`${PC_ASSISTIVE_BUTTON_CLASS} w-[143px]`}
            onClick={() => navigate(`/orgs/new/${ORG_LAST_STEP}`)}
          >
            이전
          </button>
          <button
            type="button"
            className={`${PC_PRIMARY_BUTTON_CLASS} w-[250px]`}
            disabled={!draft.canGoNext}
            onClick={draft.goNext}
          >
            다음
          </button>
        </>
      }
    >
      <section className="flex w-full flex-col items-start gap-6">
        <SectionHeader
          no="01"
          title="해결카드 만들어 주세요."
          desc="실제로 제공하는 서비스를 작성해 주세요."
        />
        {/* 라벨 줄 높이가 1.7 인 것까지 시안 값이다(1288:21962) — 기업 소개 쪽(1.35)과 다르다. */}
        <div className="flex w-full flex-col gap-6 [&_label>span]:leading-[1.7]">
          <TextField
            size="large"
            label="해결카드 이름"
            placeholder="예: 어르신 방문 말벗 프로그램"
            value={draft.cardTitle}
            maxLength={CARD_TITLE_MAX}
            onChange={(e) => draft.setCardTitle(e.target.value)}
          />
          <PcTextarea
            label="한 줄 요약"
            placeholder="예: 정기적인 말벗 활동으로 혼자 지내는 어르신의 외로움을 줄이고 일상 속 정서적 연결을 돕는 프로그램"
            value={draft.cardSummary}
            onChange={draft.setCardSummary}
            maxLength={CARD_SUMMARY_MAX}
            count={{ current: draft.cardSummary.length, max: CARD_SUMMARY_MAX }}
          />
          <PcTextarea
            label="어떤 문제를 해결하나요?"
            placeholder="예: 혼자 지내는 어르신이 대화할 기회와 사회적 관계가 부족해 외로움과 정서적 고립을 겪고 있습니다."
            value={draft.cardProblem}
            onChange={draft.setCardProblem}
            maxLength={CARD_TEXT_MAX}
          />
          <PcTextarea
            label="어떻게 해결할 수 있나요?"
            placeholder="예: 교육받은 활동가가 어르신과 정기적으로 만나거나 전화로 안부를 묻고 대화를 나누며 지속적인 관계 형성을 지원합니다."
            value={draft.cardBody}
            onChange={draft.setCardBody}
            maxLength={CARD_TEXT_MAX}
          />
          <PcTextarea
            label="운영상 한계 혹은 필수 조건이 있다면 작성해주세요."
            placeholder="예: 평일 낮만 가능, 최소 5명 이상 등"
            value={draft.cardAccess}
            onChange={draft.setCardAccess}
            maxLength={CARD_ACCESS_MAX}
          />
        </div>
      </section>
    </OrgNewPcShell>
  )
}
